import { useMemo } from "react";

import {
	PaymentMethod,
	Reservation_List_Reservation_Status,
	Reservation_List_Reservation_Type,
} from "@/lib/gql/__generated__/graphql";

import { isActiveReservationStatus } from "./is-active-reservation-status";

import type * as Repository from "../repository";
import type * as Type from "../type";

type QueryData = Repository.Query.QueryData.ReservationListQueryData;
type SearchModel = Type.SearchModel;

type Props = {
	queryData: QueryData;
	searchModel: SearchModel;
};
type Result = {
	queryData: QueryData;
};

export const useFilteredQueryDataBySearchModel = ({ queryData: _queryData, searchModel }: Props): Result => {
	/**
	 * フリーワードでの絞り込み（空白区切り）
	 */
	const filteredQueryDataByFreeWordText = useMemo(() => {
		const freeWordText = searchModel.freeWordText;
		if (!freeWordText) return _queryData;

		/**
		 * フリーワードの空白区切り
		 */
		const splittedFreeWordTexts = freeWordText.split(" ");

		const filtered = _queryData.reservationList.filter(
			({
				reservationID,
				guestName,
				checkIn,
				checkOut,
				reserveDate,
				totalGuestCharge,
				numOfPeople,
				room,
				plan,
				discount,
				guestTel,
				guestEmail,
				guestAddress,
			}) =>
				splittedFreeWordTexts.some(
					(text) =>
						reservationID.includes(text) ||
						checkIn.includes(text) ||
						checkOut.includes(text) ||
						reserveDate.includes(text) ||
						totalGuestCharge.toString().includes(text) ||
						numOfPeople.toString().includes(text) ||
						room.some(({ name }) => name.includes(text)) ||
						plan?.includes(text) ||
						discount?.includes(text) ||
						guestName.includes(text) ||
						guestEmail.includes(text) ||
						guestTel.includes(text) ||
						guestAddress.includes(text),
				),
		);

		return {
			..._queryData,
			reservationList: filtered,
		};
	}, [searchModel.freeWordText, _queryData]);
	/**
	 * アクティブな予約に絞り込む
	 */
	const filteredQueryDataByIsActive = useMemo(() => {
		const isActive = searchModel.isActiveChecked;
		if (!isActive) return filteredQueryDataByFreeWordText;

		const filtered = filteredQueryDataByFreeWordText.reservationList.filter(
			({ reservationStatus }) => isActiveReservationStatus(reservationStatus),
		);

		return {
			..._queryData,
			reservationList: filtered,
		};
	}, [searchModel.isActiveChecked, filteredQueryDataByFreeWordText, _queryData]);
	/**
	 * キャンセル済みの予約に絞り込む
	 */
	const filteredQueryDataByIsCanceled = useMemo(() => {
		const isCanceled = searchModel.isCanceledChecked;
		if (!isCanceled) return filteredQueryDataByIsActive;

		const filtered = filteredQueryDataByIsActive.reservationList.filter(
			({ reservationStatus }) =>
				reservationStatus === Reservation_List_Reservation_Status.Cancelled ||
				reservationStatus === Reservation_List_Reservation_Status.CancelledByPaymentError,
		);

		return {
			...filteredQueryDataByIsActive,
			reservationList: filtered,
		};
	}, [searchModel.isCanceledChecked, filteredQueryDataByIsActive]);
	/**
	 * メルマガ配信希望者の予約に絞り込む
	 */
	const filteredQueryDataByIsAcceptMailMagazine = useMemo(() => {
		const isAcceptMailMagazine = searchModel.isAcceptMailMagazineChecked;
		if (!isAcceptMailMagazine) return filteredQueryDataByIsCanceled;

		const filtered = filteredQueryDataByIsCanceled.reservationList.filter(
			({ isAcceptMailMagazine }) => isAcceptMailMagazine,
		);

		return {
			...filteredQueryDataByIsCanceled,
			reservationList: filtered,
		};
	}, [searchModel.isAcceptMailMagazineChecked, filteredQueryDataByIsCanceled]);
	/**
	 * 現地決済者の予約に絞り込む
	 */
	const filteredQueryDataByIsLocalPayment = useMemo(() => {
		const isLocalPayment = searchModel.isLocalPaymentChecked;
		if (!isLocalPayment) return filteredQueryDataByIsAcceptMailMagazine;

		const filtered = filteredQueryDataByIsAcceptMailMagazine.reservationList.filter(
			(reservation) => reservation.paymentMethod === PaymentMethod.Local,
		);

		return {
			...filteredQueryDataByIsAcceptMailMagazine,
			reservationList: filtered,
		};
	}, [searchModel.isLocalPaymentChecked, filteredQueryDataByIsAcceptMailMagazine]);
	/**
	 * 直接入力した予約（ホストが入力）に絞り込む
	 */
	const filteredQueryDataByIsHostReservation = useMemo(() => {
		const isHostReservation = searchModel.isHostReservationChecked;
		if (!isHostReservation) return filteredQueryDataByIsLocalPayment;

		const filtered = filteredQueryDataByIsLocalPayment.reservationList.filter(({ reservationType }) =>
			reservationType.includes(Reservation_List_Reservation_Type.HostReservation),
		);

		return {
			...filteredQueryDataByIsLocalPayment,
			reservationList: filtered,
		};
	}, [searchModel.isHostReservationChecked, filteredQueryDataByIsLocalPayment]);
	/**
	 * 日帰り予約に絞り込む
	 */
	const filteredQueryDataByIsDayTrip = useMemo(() => {
		const isDayTrip = searchModel.isDayTripChecked;
		if (!isDayTrip) return filteredQueryDataByIsHostReservation;

		const filtered = filteredQueryDataByIsHostReservation.reservationList.filter(({ reservationType }) =>
			reservationType.includes(Reservation_List_Reservation_Type.DayTrip),
		);

		return {
			...filteredQueryDataByIsHostReservation,
			reservationList: filtered,
		};
	}, [searchModel.isDayTripChecked, filteredQueryDataByIsHostReservation]);
	/**
	 * 予約内容/プランでの検索条件に絞り込む
	 */
	const filteredQueryDataByReservationPlans = useMemo(() => {
		const activePlans = searchModel.reservation.plans.filter(({ checked }) => checked);
		if (activePlans.length === 0) return filteredQueryDataByIsDayTrip;

		const filtered = filteredQueryDataByIsDayTrip.reservationList.filter(({ plan }) =>
			activePlans.some(({ id }) => plan === id),
		);

		return {
			...filteredQueryDataByIsDayTrip,
			reservationList: filtered,
		};
	}, [searchModel.reservation.plans, filteredQueryDataByIsDayTrip]);
	/**
	 * 予約内容/部屋での検索条件に絞り込む
	 */
	const filteredQueryDataByReservationRooms = useMemo(() => {
		const activeRooms = searchModel.reservation.rooms.filter(({ checked }) => checked);
		if (activeRooms.length === 0) return filteredQueryDataByReservationPlans;

		/**
		 * @example
		 * ```
		 * type ActiveRoom = {
		 *   id: string; // room.name === id
		 *   label: string;
		 *   checked: boolean;
		 * }
		 * ```
		 */
		const filtered = filteredQueryDataByReservationPlans.reservationList.filter(({ room }) =>
			activeRooms.some(({ id }) => room.some((item) => item.name === id)),
		);

		return {
			...filteredQueryDataByReservationPlans,
			reservationList: filtered,
		};
	}, [searchModel.reservation.rooms, filteredQueryDataByReservationPlans]);
	/**
	 * 予約内容/オプションでの検索条件に絞り込む
	 */
	const filteredQueryDataByReservationOptions = useMemo(() => {
		const activeOptions = searchModel.reservation.options.filter(({ checked }) => checked);
		if (activeOptions.length === 0) return filteredQueryDataByReservationRooms;

		/**
		 * @example
		 * ```
		 * type ActiveOption = {
		 *    id: string; // option.name === id
		 *    label: string;
		 *    checked: boolean;
		 * }
		 * ```
		 */
		const filtered = filteredQueryDataByReservationRooms.reservationList.filter(({ option }) =>
			activeOptions.some(({ id }) => option.some(({ name }) => id === name)),
		);

		return {
			...filteredQueryDataByReservationRooms,
			reservationList: filtered,
		};
	}, [searchModel.reservation.options, filteredQueryDataByReservationRooms]);

	/**
	 * 予約内容/ディスカウントでの検索条件に絞り込む
	 */
	const filteredQueryDataByReservationDiscounts = useMemo(() => {
		const activeDiscounts = searchModel.reservation.discounts.filter(({ checked }) => checked);
		if (activeDiscounts.length === 0) return filteredQueryDataByReservationOptions;

		const filtered = filteredQueryDataByReservationOptions.reservationList.filter(({ discount }) =>
			activeDiscounts.some(({ id }) => discount === id),
		);

		return {
			...filteredQueryDataByReservationOptions,
			reservationList: filtered,
		};
	}, [searchModel.reservation.discounts, filteredQueryDataByReservationOptions]);

	return {
		queryData: filteredQueryDataByReservationDiscounts,
	};
};
